'use client';

import Image from 'next/image';
import Link from 'next/link';
import { Building2, Calendar, Clock, MapPin } from 'lucide-react';
import { AttendeeAvatars } from './AttendeeAvatars';
import { Language, Translations } from '@/context/LanguageContext';
import { getStrapiImageUrl, shouldUnoptimizeNextImage } from '@/lib/image-utils';
import { getEventCategoryLabelI18n } from '@/lib/taxonomy-labels';
import { Event, MockUser, getEventCategories } from '@/types';
import { formatTime12H, getEventCostLabel, highlightText, parseDateOnly } from '@/utils';

interface EventCardProps {
  event: Event;
  users: MockUser[];
  t: Translations;
  language: Language;
  searchQuery?: string;
}

// MWBI-903 follow-up — matches against the translated category label (not
// just the raw English `category.name`), so a Spanish-mode user searching
// for the Spanish label they see on screen still finds matching events.
export function eventMatchesSearch(event: Event, searchQuery: string, t: Translations): boolean {
  const query = searchQuery.trim().toLowerCase();
  const eventCategories = getEventCategories(event);

  if (!query) return true;

  return (
    event.title.toLowerCase().includes(query) ||
    event.location.toLowerCase().includes(query) ||
    (event.description?.toLowerCase().includes(query) ?? false) ||
    (event.organizer?.toLowerCase().includes(query) ?? false) ||
    eventCategories.some((category) =>
      getEventCategoryLabelI18n(t, category.slug, category.name).toLowerCase().includes(query)
    )
  );
}

export function EventCard({ event, users, t, language, searchQuery = '' }: EventCardProps) {
  const imageUrl = getStrapiImageUrl(event.image?.url || event.images?.[0]?.url);
  const eventHref = `/events/${event.documentId}`;
  const formattedDate = parseDateOnly(event.date).toLocaleDateString(
    language === 'es' ? 'es-ES' : 'en-US',
    {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
    }
  );

  return (
    <Link href={eventHref} className="block group">
      <article className="bg-background border border-border rounded-lg overflow-hidden hover:shadow-md transition-shadow h-full">
        <div className="relative h-62 bg-background-secondary flex items-center justify-center">
          {imageUrl ? (
            <Image src={imageUrl} alt={event.title} fill className="object-cover" unoptimized={shouldUnoptimizeNextImage(imageUrl)} />
          ) : (
            <Image src="/NoImage.jpg" alt={event.title} fill className="object-cover" />
          )}
        </div>

        <div className="p-4 space-y-3">
          <h3 className="font-semibold text-foreground text-lg sm:text-base line-clamp-2">
            {highlightText(event.title, searchQuery)}
          </h3>

          <div className="space-y-2 text-base sm:text-sm text-foreground-muted">
            <div className="flex items-center gap-2">
              <Calendar size={14} className="shrink-0 text-primary" />
              <span>{formattedDate}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock size={14} className="shrink-0" />
              <span data-testid="event-card-time">{formatTime12H(event.timeStart)} - {formatTime12H(event.timeEnd)}</span>
            </div>
            <div className="flex items-center gap-2">
              <MapPin size={14} className="shrink-0" />
              <span className="line-clamp-1">{highlightText(event.location, searchQuery)}</span>
            </div>
            {event.group && (
              <div className="flex items-center gap-2">
                <Building2 size={14} className="shrink-0" />
                <span>{event.group.name}</span>
              </div>
            )}
            <AttendeeAvatars attendees={event.attendees || []} users={users} maxVisible={3} size="sm" />
          </div>

          <p className="pt-1 text-base sm:text-sm font-medium text-foreground" data-testid="event-card-cost">
            {getEventCostLabel(event, t)}
          </p>
        </div>
      </article>
    </Link>
  );
}
